/**
 * Prezzo e ribasso ▸ Configurazioni salvate — the list's two drills (issue 226, giro 3 «Dopo»
 * point 2, `docs/adr/6002` amendment 2026-10-07). The pure half: the addresses and their parses,
 * tested on strings (CLAUDE.md §3.4, rule 4).
 *
 *   `/inseason/markdown/configurations/:configurationId`       one configuration: its versions,
 *                                                              recall, a new version, archive
 *   `/inseason/markdown/configurations/compare?a=…&b=…`         two versions, parameter by parameter
 *
 * `compare` is a static segment, so the router resolves it before `:configurationId` (static
 * first, `resolveRoute`) — a configuration can never be named «compare» into the wrong page.
 *
 * Every address keeps the page's query: the question on the URL is what «Save as version …» saves
 * and what a recall rewrites, so a drill that dropped it would save nothing, or the wrong thing.
 * A version is `<configuration_id>:<version>` — the id is the platform's opaque key, split at the
 * LAST colon, so an id containing one still parses.
 */

import { MARKDOWN_VIEW_PATHS } from './markdown-views';

export const CONFIGURATIONS_PATH = MARKDOWN_VIEW_PATHS.configurations;
export const COMPARE_PATH = `${CONFIGURATIONS_PATH}/compare`;
export const COMPARE_A_PARAM = 'a';
export const COMPARE_B_PARAM = 'b';

/** One saved version: the configuration and its number. */
export interface VersionPick {
  readonly configurationId: string;
  readonly version: number;
}

export const samePick = (a: VersionPick, b: VersionPick): boolean =>
  a.configurationId === b.configurationId && a.version === b.version;

/** The query string, the compare keys removed — the question, and whatever else the page carried. */
function scopeOf(search: string): URLSearchParams {
  const params = new URLSearchParams(search);
  params.delete(COMPARE_A_PARAM);
  params.delete(COMPARE_B_PARAM);
  return params;
}

function withQuery(path: string, params: URLSearchParams): string {
  const query = params.toString();
  return query === '' ? path : `${path}?${query}`;
}

/** The configuration's own page, the page's question kept. */
export function configurationHref(configurationId: string, search: string): string {
  return withQuery(`${CONFIGURATIONS_PATH}/${encodeURIComponent(configurationId)}`, scopeOf(search));
}

/** The list, the page's question kept — the drill's breadcrumb. */
export function configurationsHref(search: string): string {
  return withQuery(CONFIGURATIONS_PATH, scopeOf(search));
}

export function pickValue(pick: VersionPick): string {
  return `${pick.configurationId}:${pick.version}`;
}

export function readPick(value: string | null): VersionPick | null {
  if (value === null) return null;
  const at = value.lastIndexOf(':');
  if (at <= 0) return null;
  const configurationId = value.slice(0, at).trim();
  const digits = value.slice(at + 1);
  if (configurationId === '' || !/^[1-9]\d*$/.test(digits)) return null;
  return { configurationId, version: Number(digits) };
}

/** The comparison of two versions, the page's question kept. */
export function compareHref(a: VersionPick, b: VersionPick, search: string): string {
  const params = scopeOf(search);
  params.set(COMPARE_A_PARAM, pickValue(a));
  params.set(COMPARE_B_PARAM, pickValue(b));
  return withQuery(COMPARE_PATH, params);
}

/** The two versions the comparison names; either is `null` when missing or malformed. Nothing is defaulted. */
export function readComparePicks(search: string): { readonly a: VersionPick | null; readonly b: VersionPick | null } {
  const params = new URLSearchParams(search);
  return { a: readPick(params.get(COMPARE_A_PARAM)), b: readPick(params.get(COMPARE_B_PARAM)) };
}

/** The configuration a drill path names, or `null` — never the comparison's static segment. */
export function readConfigurationDrill(pathname: string): string | null {
  const prefix = `${CONFIGURATIONS_PATH}/`;
  const path = pathname.replace(/\/+$/, '');
  if (!path.startsWith(prefix)) return null;
  const rest = path.slice(prefix.length);
  if (rest === '' || rest.includes('/') || rest === 'compare') return null;
  try {
    const id = decodeURIComponent(rest);
    return id.trim() === '' ? null : id;
  } catch {
    return null;
  }
}

/**
 * Two picks at most: a third replaces the older of the two, so the newest choice always lands; a
 * pick already chosen is un-chosen.
 */
export function togglePick(current: readonly VersionPick[], pick: VersionPick): readonly VersionPick[] {
  if (current.some((p) => samePick(p, pick))) return current.filter((p) => !samePick(p, pick));
  return [...current, pick].slice(-2);
}
